import { useState } from 'react'
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, Grid, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { trackActivity } from '../../../../utils/mixpanel';
import { regexConstants } from '../../../../constants/regEx';
import { globalPostService, globalPutService } from '../../../../utils/globalApiServices';
import { addHolidayRulesApi, updateHolidayRulesApi } from '../apiService';
import { checkApiStatus } from '../../../../utils';

const OVERRIDE_TYPE_OPTIONS = [{ label: 'Working Day', value: 1 }, { label: 'Non-Working Day', value: 2 }];
const WEEKDAY_OPTIONS = [{ label: 'Monday', value: 1 }, { label: 'Tuesday', value: 2 }, { label: 'Wednesday', value: 3 }, { label: 'Thursday', value: 4 }, { label: 'Friday', value: 5 }, { label: 'Saturday', value: 6 }, { label: 'Sunday', value: 7 }];
const APPLIES_TO_OPTIONS = [{ label: 'All Months', value: 0 }, { label: 'January', value: 1 }, { label: 'February', value: 2 }, { label: 'March', value: 3 }, { label: 'April', value: 4 }, { label: 'May', value: 5 }, { label: 'June', value: 6 }, { label: 'July', value: 7 }, { label: 'August', value: 8 }, { label: 'September', value: 9 }, { label: 'October', value: 10 }, { label: 'November', value: 11 }, { label: 'December', value: 12 }];

const HolidayRulesCru = ({ holidayRulesCRU, onToggleModal, calenderSlug, modeType, getResponseBack }) => {
    const { enqueueSnackbar } = useSnackbar();
    const mapOccurrenceArrayToObject = (arr = []) => { const obj = {}; arr.forEach(n => { obj[n] = true; }); return obj; };
    const [data, setData] = useState({ ...holidayRulesCRU?.data, occurrence: mapOccurrenceArrayToObject( holidayRulesCRU?.data?.occurrence ) });
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState({});

    const onFieldChange = (keyParam, value) => {
        setData({ ...data, [keyParam]: value })
    }

    const onOccurrenceChange = (count, checked) => {
        setData(prev => ({
            ...prev,
            occurrence: {
                ...prev.occurrence,
                [count]: checked
            }
        }));
    };

    const onAddEditHolidayList = () => {
        let validationInputs = {
            holiday_name: data?.holiday_name?.trim() ? "" : "Please enter Name",
            weekday: data?.weekday ? "" : "Please select Weekday",
            override_type: data?.override_type ? "" : "Please select Override Type",
            applies_to: data?.applies_to ? "" : "Please select Applies To"
        };

        if (!Object.values(validationInputs).every(v => v === "")) {
            setError(validationInputs);
            trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Exceptional Working Day`, error_msg: validationInputs, })
            return;
        }

        const occurrenceArray = Object.entries(data.occurrence).filter(([_, checked]) => checked).map(([key]) => Number(key));

        if (!occurrenceArray.length) {
            enqueueSnackbar(
                "Please select at least one Occurrence",
                { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
            );
            return;
        }

        const payload = {
            holiday_name: data?.holiday_name || null,
            calendar_slug: calenderSlug,
            weekday: data?.weekday,
            override_type: data?.override_type,
            applies_to: data?.applies_to,
            occurrence: occurrenceArray
        };

        setLoading(true);
        const apiCall = data?.id
            ? globalPutService(updateHolidayRulesApi({ holiday_slug: data?.slug, calenderSlug }), payload)
            : globalPostService(addHolidayRulesApi({ calenderSlug }), payload);

        apiCall.then(response => {
            setLoading(false);
            if (checkApiStatus(response)) {
                enqueueSnackbar(response?.data?.message, { variant: "success" });
                onToggleModal();
                getResponseBack();
                trackActivity(data?.id ? 'Item Edited' : 'Item Added', {
                    page_title: 'Calendar Detail Exceptional Working Days',
                    payload
                });
            } else {
                enqueueSnackbar(response?.data?.message, { variant: "error" });
            }
        });
    };

    const onRestErrorKey = (keyParam) => {
        setError(prev => ({ ...prev, [keyParam]: '' }))
    }

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Exceptional Working Days`, page_type: page_type, })
    }
    return (
        <>
            <Dialog aria-labelledby="customized-dialog-title" className="add-lease-dialog" maxWidth="md" open={true} fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, pl: 3 }} className='dialog-header'>
                    {modeType === "add" ? "Add Exceptional Working Day" : modeType === "edit" ? "Edit Exceptional Working Day" : "View Exceptional Working Day"}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <TextField
                                disabled={modeType === "view"}
                                id="holiday_name"
                                label="Name"
                                variant="outlined"
                                size="small"
                                margin="normal"
                                onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange('holiday_name', e.target.value.trimStart()) : e.preventDefault() }}
                                onFocus={() => onRestErrorKey("holiday_name")}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                                value={data?.holiday_name || ''}
                                error={!!error.holiday_name}
                                helperText={error.holiday_name}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={OVERRIDE_TYPE_OPTIONS}
                                disabled={modeType === 'view'}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                getOptionLabel={(option) => option.label || ""}
                                value={data?.override_type || null}
                                onChange={(_, value) => onFieldChange("override_type", value)}
                                onFocus={() => onRestErrorKey("override_type")}
                                renderInput={(params) => (
                                    <TextField
                                        required
                                        {...params}
                                        label="Override Type"
                                        margin='normal'
                                        size='small'
                                        error={Boolean(error.override_type)}
                                        helperText={error.override_type}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        disabled={modeType === 'view'}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={WEEKDAY_OPTIONS}
                                disabled={modeType === 'view'}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                getOptionLabel={(option) => option.label || ""}
                                value={data?.weekday || null}
                                onChange={(_, value) => onFieldChange("weekday", value)}
                                onFocus={() => onRestErrorKey("weekday")}
                                slotProps={{ listbox: { style: { maxHeight: 170 } } }}
                                renderInput={(params) => (
                                    <TextField
                                        required
                                        {...params}
                                        label="Weekday"
                                        margin='normal'
                                        size='small'
                                        error={Boolean(error.weekday)}
                                        helperText={error.weekday}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        disabled={modeType === 'view'}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={APPLIES_TO_OPTIONS}
                                disabled={modeType === 'view'}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                getOptionLabel={(option) => option.label || ""}
                                value={data?.applies_to || null}
                                onChange={(_, value) => onFieldChange("applies_to", value)}
                                onFocus={() => onRestErrorKey("applies_to")}
                                slotProps={{ listbox: { style: { maxHeight: 180 } } }}
                                renderInput={(params) => (
                                    <TextField
                                        required
                                        {...params}
                                        label="Applies To"
                                        margin='normal'
                                        size='small'
                                        error={Boolean(error.applies_to)}
                                        helperText={error.applies_to}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        disabled={modeType === 'view'}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <Typography className='section-header-text'>Occurrence</Typography>
                            <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', ml: '-10px' }}>
                                {[1, 2, 3, 4, 5].map(count => (
                                    <Box key={count} className="check-item-div">
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(data?.occurrence?.[count])}
                                            onChange={(e) =>
                                                onOccurrenceChange(count, e.target.checked)
                                            }
                                            disabled={modeType === 'view'}
                                        />
                                        <span className="check-item-span file-name">
                                            {count}{["st", "nd", "rd"][count - 1] || "th"}
                                        </span>
                                    </Box>
                                ))}
                            </FormGroup>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "15px" }}>
                    <Button className='cancel-btn' disabled={isLoading} variant="outlined" size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
                    {modeType === "view" ? null :
                        <Button variant="contained" size='small' className="btn-primary" onClick={() => { handleItemClicked('Save Button'); onAddEditHolidayList() }}>
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : "SAVE"}
                        </Button>}
                </DialogActions>
            </Dialog>
        </>
    )
}

export default HolidayRulesCru